import { useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import {
  Mail,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  X,
  Send,
  Ban,
  Clock,
  ChevronDown,
  Filter,
  UserPlus,
} from 'lucide-react';
import { membersApi } from '../../../api/client';
import type { OrgInvitation, OrgRole, InvitationStatus } from '../../../api/client';
import { RoleBadge } from '../../../components/team/RoleBadge';
import { usePermissions } from '../../../hooks/usePermissions';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date();
}

// ── Status Badge ───────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<InvitationStatus, string> = {
  pending:  'text-brand-light bg-brand/10 border-brand/30',
  accepted: 'text-win bg-win/10 border-win/30',
  expired:  'text-gray-500 bg-gray-500/10 border-gray-500/30',
  revoked:  'text-loss bg-loss/10 border-loss/30',
};

const STATUS_ICONS: Record<InvitationStatus, React.ElementType> = {
  pending:  Clock,
  accepted: CheckCircle,
  expired:  AlertTriangle,
  revoked:  Ban,
};

function StatusBadge({ status }: { status: InvitationStatus }) {
  const Icon = STATUS_ICONS[status];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5',
        STATUS_STYLES[status],
      )}
    >
      <Icon className="w-3 h-3" />
      {status}
    </span>
  );
}

// ── Revoke Confirm Modal ───────────────────────────────────────────────────────

function RevokeConfirmModal({
  email,
  onClose,
  onConfirm,
  loading,
}: {
  email: string;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="w-full max-w-sm bg-surface-1 border border-surface-4 rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-loss" />
            <h2 className="text-sm font-semibold text-gray-100">Revoke Invitation</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-400">
            Revoke the invitation for{' '}
            <span className="text-gray-100 font-medium">{email}</span>? They will no longer be
            able to join using this link.
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 bg-surface-2 hover:bg-surface-3 border border-surface-4 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-loss hover:bg-loss/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Revoking…' : 'Revoke'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Invite Modal ───────────────────────────────────────────────────────────────

function InviteModal({
  onClose,
  onInvited,
  currentRole,
}: {
  onClose: () => void;
  onInvited: (inv: OrgInvitation) => void;
  currentRole: OrgRole | null;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableRoles: OrgRole[] = currentRole === 'owner'
    ? ['admin', 'member', 'viewer']
    : ['member', 'viewer'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const inv = await membersApi.invite(email.trim(), role);
      onInvited(inv);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full max-w-md bg-surface-1 border border-surface-4 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-4">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-brand" />
            <h2 className="text-sm font-semibold text-gray-100">Send Invitation</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              required
              autoFocus
              className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Role</label>
            <div className="relative">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as OrgRole)}
                className="w-full appearance-none px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand/60 transition-colors"
              >
                {availableRoles.map((r) => (
                  <option key={r} value={r} className="bg-surface-2">
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
            </div>
          </div>
          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-loss/10 border border-loss/30 rounded-md">
              <AlertTriangle className="w-3.5 h-3.5 text-loss mt-0.5 flex-shrink-0" />
              <p className="text-xs text-loss">{error}</p>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-400 bg-surface-2 hover:bg-surface-3 border border-surface-4 rounded-md transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="flex-1 px-4 py-2 text-sm font-medium text-surface-1 bg-brand hover:bg-brand/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending…' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | InvitationStatus;

export function AdminInvitationsPage() {
  const { can, role: currentRole } = usePermissions();
  const canInvite = can('members.invite');

  const [invitations, setInvitations] = useState<OrgInvitation[]>([]);
  const [isLoading,   setIsLoading]   = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [search,      setSearch]      = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [success,     setSuccess]     = useState<string | null>(null);
  const [showInvite,  setShowInvite]  = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<OrgInvitation | null>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);

  const load = () => {
    setIsLoading(true);
    membersApi
      .listInvitations()
      .then(setInvitations)
      .catch((err) => setError((err as Error).message ?? 'Failed to load invitations'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return invitations.filter((inv) => {
      if (statusFilter !== 'all' && inv.status !== statusFilter) return false;
      if (q && !inv.email.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [invitations, search, statusFilter]);

  const handleRevoke = async (invitationId: string) => {
    setRevokeLoading(true);
    try {
      await membersApi.revokeInvitation(invitationId);
      setInvitations((prev) =>
        prev.map((i) => i.id === invitationId ? { ...i, status: 'revoked' as InvitationStatus } : i),
      );
      setSuccess('Invitation revoked.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invitation');
    } finally {
      setRevokeLoading(false);
      setRevokeTarget(null);
    }
  };

  const handleResend = async (invitationId: string) => {
    setActionLoading(invitationId);
    try {
      const updated = await membersApi.resendInvitation(invitationId);
      setInvitations((prev) => prev.map((i) => i.id === invitationId ? updated : i));
      setSuccess('Invitation resent.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
    { value: 'all',      label: 'All' },
    { value: 'pending',  label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'expired',  label: 'Expired' },
    { value: 'revoked',  label: 'Revoked' },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-100">Invitations</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage pending, accepted, expired, and revoked invitations.
          </p>
        </div>
        {canInvite && (
          <button
            onClick={() => setShowInvite(true)}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-surface-1 bg-brand hover:bg-brand/90 rounded-md transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            Invite
          </button>
        )}
      </div>

      {/* Feedback */}
      {success && (
        <div className="flex items-center gap-3 p-3 bg-win/10 border border-win/20 rounded-lg">
          <CheckCircle className="w-4 h-4 text-win flex-shrink-0" />
          <p className="text-sm text-win">{success}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Search + filter + refresh */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email…"
            className="w-full pl-8 pr-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60 transition-colors"
          />
        </div>
        <div className="flex items-center gap-1 bg-surface-2 border border-surface-4 rounded-md p-1">
          <Filter className="w-3.5 h-3.5 text-gray-600 ml-1" />
          {STATUS_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium transition-colors',
                statusFilter === value
                  ? 'bg-brand/20 text-brand'
                  : 'text-gray-500 hover:text-gray-300',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          className="p-2 text-gray-500 hover:text-gray-300 hover:bg-surface-3 rounded-md transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 px-5">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading invitations…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-gray-500 gap-2">
            <Mail className="w-8 h-8 text-gray-700" />
            <p className="text-sm">{search || statusFilter !== 'all' ? 'No invitations match your filters.' : 'No invitations yet.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" role="table">
              <thead>
                <tr className="border-b border-surface-4">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Invited By</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Expires</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Sent</th>
                  {canInvite && <th className="w-28 px-4 py-3" />}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => {
                  const expired = inv.status === 'pending' && isExpired(inv.expiresAt);
                  const isActing = actionLoading === inv.id;
                  return (
                    <tr
                      key={inv.id}
                      className="border-b border-surface-4/50 last:border-0 hover:bg-surface-2/30 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-surface-3 border border-surface-4 flex items-center justify-center flex-shrink-0">
                            <Mail className="w-3.5 h-3.5 text-gray-500" />
                          </div>
                          <span className="font-medium text-gray-200 truncate">{inv.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <RoleBadge role={inv.role} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={expired ? 'expired' : inv.status} />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell">
                        {inv.invitedByName ?? inv.invitedByEmail ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden md:table-cell">
                        {expired
                          ? <span className="text-loss">{formatDate(inv.expiresAt)}</span>
                          : formatDate(inv.expiresAt)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell">
                        {formatDate(inv.createdAt)}
                      </td>
                      {canInvite && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            {inv.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => void handleResend(inv.id)}
                                  disabled={isActing}
                                  className="flex items-center gap-1 px-2 py-1 text-xs text-brand-light hover:text-brand hover:bg-brand/10 rounded transition-colors disabled:opacity-50"
                                  title="Resend invitation"
                                >
                                  {isActing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                  Resend
                                </button>
                                <button
                                  onClick={() => setRevokeTarget(inv)}
                                  disabled={isActing}
                                  className="flex items-center gap-1 px-2 py-1 text-xs text-loss hover:text-loss/80 hover:bg-loss/10 rounded transition-colors disabled:opacity-50"
                                  title="Revoke invitation"
                                >
                                  <Ban className="w-3 h-3" />
                                  Revoke
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-600 text-center">
        {filtered.length} {filtered.length === 1 ? 'invitation' : 'invitations'}
        {invitations.length !== filtered.length && ` of ${invitations.length}`}
      </p>

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvited={(inv) => {
            setInvitations((prev) => [inv, ...prev]);
            setSuccess(`Invitation sent to ${inv.email}.`);
            setTimeout(() => setSuccess(null), 4000);
          }}
          currentRole={currentRole}
        />
      )}
      {revokeTarget && (
        <RevokeConfirmModal
          email={revokeTarget.email}
          loading={revokeLoading}
          onClose={() => setRevokeTarget(null)}
          onConfirm={() => void handleRevoke(revokeTarget.id)}
        />
      )}
    </div>
  );
}
